import { useQueryClient } from "@tanstack/react-query";
import {
  getGetTemplateListQueryKey,
  useCompleteReportSampleUpload,
  useGetReportSamplePartUrl,
  useStartReportSampleUpload,
} from "~/api/custom-reports/custom-reports";
import type { StartReportSampleUploadBodyContentType } from "~/api/model";
import logger from "~/utils/logger";
import {
  REPORT_SAMPLE_UPLOAD_TYPES,
  resolveUploadContentType,
} from "~/utils/uploadContentType";
import { useMultipartFileUpload } from "./useMultipartFileUpload";

interface UseReportSampleUploadParams {
  organizationId: string;
  setAsDefault?: boolean;
}

export const getReportSampleContentType = (
  contentType: string,
  filename: string
): StartReportSampleUploadBodyContentType => {
  return resolveUploadContentType(
    contentType,
    filename,
    REPORT_SAMPLE_UPLOAD_TYPES,
    `Unsupported report sample file type: ${contentType}`
  );
};

export const useReportSampleUpload = ({
  organizationId,
  setAsDefault = false,
}: UseReportSampleUploadParams) => {
  const { mutateAsync: startReportSampleUpload } = useStartReportSampleUpload();
  const { mutateAsync: getReportSamplePartUrl } = useGetReportSamplePartUrl();
  const { mutateAsync: completeReportSampleUpload } =
    useCompleteReportSampleUpload();
  const queryClient = useQueryClient();
  const { uploadFile, isUploading, uploadProgress } = useMultipartFileUpload({
    startUpload: async (uploadId, file) =>
      startReportSampleUpload({
        organizationId,
        uploadId,
        data: {
          contentType: getReportSampleContentType(file.type, file.name),
          filename: file.name,
        },
      }),
    getPartUrl: async (uploadId, key, s3UploadId, partNumber) =>
      getReportSamplePartUrl({
        organizationId,
        uploadId,
        data: { key, uploadId: s3UploadId, partNumber },
      }),
    completeUpload: async (
      uploadId,
      key,
      s3UploadId,
      filename,
      _contentType,
      _sizeBytes,
      parts
    ) =>
      completeReportSampleUpload({
        organizationId,
        uploadId,
        data: {
          key,
          uploadId: s3UploadId,
          filename,
          idempotencyKey: uploadId,
          parts,
          setAsDefault,
        },
      }),
    onUploadComplete: async () => {
      await queryClient.invalidateQueries({
        queryKey: getGetTemplateListQueryKey(organizationId),
      });
    },
  });

  const uploadReportSample = async (file: File) => {
    try {
      logger.info("🔄 Starting report sample upload", {
        filename: file.name,
      });
      const template = await uploadFile(file);
      logger.info("Report sample upload completed", {
        filename: file.name,
        templateId: template.id,
      });
      return {
        templateId: template.id,
        reportSampleUrl: template.reportSampleUrl,
      };
    } catch (error) {
      logger.error(`❌ Error uploading report sample ${file.name}:`, error);
      throw error;
    }
  };

  return {
    uploadReportSample,
    isUploading,
    uploadProgress,
  };
};
